import { useRef, useState, useCallback, useEffect } from 'react';

interface DragState {
  isDragging: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  offsetX: number;
  offsetY: number;
}

interface UseDragAndDropOptions {
  onDragStart?: (x: number, y: number) => void;
  onDrag?: (x: number, y: number, deltaX: number, deltaY: number) => void;
  onDragEnd?: (x: number, y: number) => void;
  onDrop?: (x: number, y: number, isOverDropZone: boolean) => void;
  dropZoneId?: string;
  disabled?: boolean;
}

export function useDragAndDrop(options: UseDragAndDropOptions = {}) {
  const {
    onDragStart,
    onDrag,
    onDragEnd,
    onDrop,
    dropZoneId,
    disabled = false,
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragState = useRef<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    offsetX: 0,
    offsetY: 0,
  });
  const elementRef = useRef<HTMLElement | null>(null);
  const initialPosition = useRef({ x: 0, y: 0 });

  const checkDropZone = useCallback((x: number, y: number): boolean => {
    if (!dropZoneId) return false;

    const dropZone = document.getElementById(dropZoneId);
    if (!dropZone) return false;

    const rect = dropZone.getBoundingClientRect();
    // Expand the drop zone slightly for better UX
    const padding = 20;
    return (
      x >= rect.left - padding &&
      x <= rect.right + padding &&
      y >= rect.top - padding &&
      y <= rect.bottom + padding
    );
  }, [dropZoneId]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();

    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
    elementRef.current = target;

    const rect = target.getBoundingClientRect();

    dragState.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY,
      offsetX: e.clientX - rect.left - rect.width / 2,
      offsetY: e.clientY - rect.top - rect.height / 2,
    };

    initialPosition.current = { x: rect.left, y: rect.top };

    setIsDragging(true);
    setPosition({ x: 0, y: 0 });
    onDragStart?.(e.clientX, e.clientY);
  }, [disabled, onDragStart]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragState.current.isDragging) return;

    e.preventDefault();

    const deltaX = e.clientX - dragState.current.currentX;
    const deltaY = e.clientY - dragState.current.currentY;

    const totalDeltaX = e.clientX - dragState.current.startX;
    const totalDeltaY = e.clientY - dragState.current.startY;

    dragState.current.currentX = e.clientX;
    dragState.current.currentY = e.clientY;

    setPosition({ x: totalDeltaX, y: totalDeltaY });
    onDrag?.(e.clientX, e.clientY, deltaX, deltaY);
  }, [onDrag]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragState.current.isDragging) return;

    e.preventDefault();

    const target = e.currentTarget as HTMLElement;
    target.releasePointerCapture(e.pointerId);

    dragState.current.isDragging = false;

    const isOverDropZone = checkDropZone(e.clientX, e.clientY);

    onDragEnd?.(e.clientX, e.clientY);
    onDrop?.(e.clientX, e.clientY, isOverDropZone);

    if (!isOverDropZone) {
      // Animate back to original position
      setPosition({ x: 0, y: 0 });
    }

    setIsDragging(false);
  }, [checkDropZone, onDragEnd, onDrop]);

  const handlePointerCancel = useCallback(() => {
    dragState.current.isDragging = false;
    setIsDragging(false);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dragState.current.isDragging = false;
    };
  }, []);

  const dragProps = {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    style: {
      touchAction: 'none' as const,
      cursor: isDragging ? 'grabbing' : disabled ? 'default' : 'grab',
      transform: `translate(${position.x}px, ${position.y}px)`,
      transition: isDragging ? 'none' : 'transform 0.3s ease-out',
    },
  };

  return {
    dragProps,
    isDragging,
    position,
  };
}
